import classNames from 'classnames'
import * as React from 'react'
import { Tooltip as RechartsTooltip } from 'recharts'

import { ChartWrapperContext } from './ChartWrapper'
import { CursorLine } from './Cursor'

interface TooltipRowProps {
  isActive?: boolean
  label: React.ReactNode
  dataKey: string
  payload?: any
  indicator?: React.ReactNode
  formatValue?: (value: any, payload: Record<string, any> & { dataKey: string }) => React.ReactNode
}

export function TooltipRow({ isActive, indicator, label, payload, formatValue }: TooltipRowProps) {
  return (
    <>
      <div className={classNames('flex justify-center', { 'mr-2': !!indicator })}>{indicator}</div>
      <div className={classNames({ 'mr-2 text-default font-medium': isActive })}>{label}</div>
      <div className="text-right tabular-nums text-default">
        {formatValue?.(payload?.value, payload ?? {}) ?? payload?.value}
      </div>
    </>
  )
}

interface TooltipInnerProps extends React.ComponentProps<typeof RechartsTooltip> {
  className?: string
  formatTitle: (value: any) => React.ReactNode
  formatValue?: (value: any, payload: Record<string, any> & { dataKey: string }) => React.ReactNode
}

function TooltipInner({ payload, formatTitle, formatValue, ...props }: React.PropsWithChildren<TooltipInnerProps>) {
  const isActive = React.useContext(ChartWrapperContext)

  return (
    <div
      className={classNames(
        props.className,
        'relative -top-8 rounded-md border border-secondary bg-primary px-3.5 pb-2.5 pt-3.5 text-left font-sans text-2xs leading-4 text-muted-2 shadow-md',
        { 'opacity-0': !isActive }
      )}
    >
      {props.label && (
        <div className="mb-1.5 text-sm text-default font-medium dark:text-gray-200">
          {formatTitle?.(props.label) ?? props.label}
        </div>
      )}

      <div className="grid grid-cols-[max-content,1fr,max-content] items-baseline gap-y-2">
        {React.Children.map(props.children, (child) => {
          if (!React.isValidElement(child) || child.type !== TooltipRow) return child

          const rowPayload = payload?.find((payload) => payload.dataKey === child.props.dataKey)

          return React.cloneElement(child, {
            payload: rowPayload,
            formatValue: child.props.formatValue ?? formatValue,
          } as TooltipRowProps)
        })}
      </div>
    </div>
  )
}

export class Tooltip extends React.Component<React.PropsWithChildren<TooltipInnerProps>> {
  static displayName = RechartsTooltip.displayName

  static defaultProps = {
    ...RechartsTooltip.defaultProps,
    offset: 2,
    position: { y: 36 },
    isAnimationActive: false,
    wrapperStyle: { outline: 'none', zIndex: 10 },
    cursor: <CursorLine />,
  }

  render() {
    const { className, formatTitle, formatValue, children, ...props } = this.props

    return (
      <RechartsTooltip
        {...props}
        content={
          <TooltipInner className={className} formatTitle={formatTitle} formatValue={formatValue}>
            {children}
          </TooltipInner>
        }
      />
    )
  }
}
